import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://admin.1oneplus.net/api';

const getAuthToken = () => {
  const token = localStorage.getItem("token");
  if (token) return token;

  const user = JSON.parse(localStorage.getItem("userTools"));
  return user?.token;
};

const createAuthHeader = () => {
  const token = getAuthToken();
  const sessionId = sessionStorage.getItem('sessionId');
  const headers = {};

  // Explicitly handle invalid/placeholder tokens
  if (token && token.trim() !== 'social_login_placeholder') {
    headers.Authorization = `Bearer ${token}`;
  } else {
    // Ensure no Authorization header is sent for guests/social placeholders
    // This overrides any global defaults if they exist
    headers.Authorization = null;
  }

  if (sessionId && sessionId !== 'null' && sessionId !== 'undefined') {
    headers['X-Session-Id'] = sessionId;
  }

  return headers;
};

// Add to Cart (By Product)
export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async ({ product_id, qty, language }, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': language || 'en'
      };

      const response = await axios.post(
        `${BASE_URL}/cart/add`,
        { product_id, qty },
        { headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Add to Cart (By Variants)
export const addToCartByVariants = createAsyncThunk(
  "cart/addToCartByVariants",
  async ({ variants, language }, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': language || 'en'
      };

      const response = await axios.post(
        `${BASE_URL}/cart/add`,
        { variants },
        { headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const fetchCartItems = createAsyncThunk(
  "cart/fetchCartItems",
  async (lang, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': lang || 'en'
      };

      // Even for session-based cart, we need headers
      const response = await axios.get(
        `${BASE_URL}/cart`,
        { headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Error fetching cart items" });
    }
  }
);

export const incrementQuantity = createAsyncThunk(
  "cart/incrementQuantity",
  async ({ cartItemId, language }, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': language || 'en'
      };

      const response = await axios.post(
        `${BASE_URL}/cart/${cartItemId}/inc`,
        {},
        { headers }
      );
      return { cartItemId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Error incrementing quantity" });
    }
  }
);

export const decrementQuantity = createAsyncThunk(
  "cart/decrementQuantity",
  async ({ cartItemId, language }, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': language || 'en'
      };

      const response = await axios.post(
        `${BASE_URL}/cart/${cartItemId}/dec`,
        {},
        { headers }
      );
      return { cartItemId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Error decrementing quantity" });
    }
  }
);

export const removeCartItem = createAsyncThunk(
  "cart/removeCartItem",
  async ({ id, language }, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': language || 'en'
      };

      await axios.delete(
        `${BASE_URL}/cart/${id}`,
        { headers }
      );
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Error deleting cart item" });
    }
  }
);

export const removeCart = createAsyncThunk(
  "cart/removeCart",
  async (language, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': language || 'en'
      };

      const response = await axios.delete(
        `${BASE_URL}/cart`,
        { headers }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Error clearing cart" });
    }
  }
);

export const removeCoupon = createAsyncThunk(
  'cart/removeCoupon',
  async ({ coupon_code, language }, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': language || 'en'
      };

      const response = await axios.post(
        `${BASE_URL}/cart/remove-coupon`,
        { coupon_code },
        { headers }
      );

      return response.data.message;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to remove coupon"
      });
    }
  }
);

export const applyCoupon = createAsyncThunk(
  'cart/applyCoupon',
  async ({ coupon_code, language }, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': language || 'en'
      };
      const response = await axios.post(
        `${BASE_URL}/cart/summary`,
        { coupon_code },
        { headers }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to apply coupon',
      });
    }
  }
);

export const fetchCartSummary = createAsyncThunk(
  'cart/fetchCartSummary',
  async (language, { rejectWithValue }) => {
    try {
      const headers = {
        ...createAuthHeader(),
        'Accept-Language': language || 'en'
      };
      const response = await axios.post(
        `${BASE_URL}/cart/summary`,
        {},
        { headers }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to fetch cart summary',
      });
    }
  }
);
